import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Calculator, Lock, Mail, User, Building } from 'lucide-react';
import ValidatedInput from '../components/ValidatedInput';
import ValidatedSelect from '../components/ValidatedSelect';
import TurnstileWidget, { isTurnstileEnabled } from '../components/TurnstileWidget';
import { validationRules } from '../utils/validation';
import '../components/ValidatedInput.css';

const Register = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError
  } = useForm();

  const password = watch('password');

  // Generate device fingerprint
  const getDeviceFingerprint = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
    const fingerprint = canvas.toDataURL();
    return btoa(fingerprint).substring(0, 20) + navigator.userAgent.substring(0, 20);
  };

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    if (isTurnstileEnabled() && !turnstileToken) {
      setError('root', { message: 'Please complete the verification check' });
      return;
    }

    setIsLoading(true);
    try {
      // Map frontend field names to backend field names
      const userData = {
        email: data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
        filing_status: data.filingStatus || 'married_filing_jointly',
        // Bot protection fields
        _timestamp: Date.now(),
        _fingerprint: getDeviceFingerprint(),
        ...(turnstileToken && { 'cf-turnstile-response': turnstileToken })
      };
      
      const result = await registerUser(userData);
      if (result.success) {
        navigate('/login');
      } else {
        setError('root', { message: result.message });
      }
    } catch (error) {
      setError('root', { message: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
            <Calculator className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Create your WealthCore account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Start managing your wealth with tax optimization, stock analysis, real estate, and retirement planning
          </p>
        </div>

        {/* Registration Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* First Name Field */}
            <ValidatedInput
              name="firstName"
              label="First Name"
              type="text"
              register={register}
              errors={errors}
              placeholder="Enter your first name"
              icon={User}
              required
              autoComplete="given-name"
              {...register('firstName', {
                ...validationRules.name,
                required: 'First name is required'
              })}
            />

            {/* Last Name Field */}
            <ValidatedInput
              name="lastName"
              label="Last Name"
              type="text"
              register={register}
              errors={errors}
              placeholder="Enter your last name"
              icon={User}
              required
              autoComplete="family-name"
              {...register('lastName', {
                ...validationRules.name,
                required: 'Last name is required'
              })}
            />

            {/* Email Field */}
            <ValidatedInput
              name="email"
              label="Email address"
              type="email"
              register={register}
              errors={errors}
              placeholder="Enter your email"
              icon={Mail}
              required
              autoComplete="email"
              {...register('email', validationRules.email)}
            />

            {/* Filing Status Field */}
            <ValidatedSelect
              name="filingStatus"
              label="Filing Status"
              register={register}
              errors={errors}
              placeholder="Select filing status"
              icon={Building}
              required
              options={[
                { value: 'single', label: 'Single' },
                { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
                { value: 'married_filing_separately', label: 'Married Filing Separately' },
                { value: 'head_of_household', label: 'Head of Household' },
                { value: 'qualifying_widow', label: 'Qualifying Widow(er)' }
              ]}
              {...register('filingStatus', {
                required: 'Filing status is required'
              })}
            />

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  {...register('password', validationRules.password)}
                  className={`appearance-none relative block w-full px-3 py-3 pl-10 pr-10 border ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center z-10"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <div className="mt-1 flex items-center gap-1 text-sm text-red-600">
                  <span>{errors.password.message}</span>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: value => value === password || 'Passwords do not match'
                  })}
                  className={`appearance-none relative block w-full px-3 py-3 pl-10 pr-10 border ${
                    errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center z-10"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <div className="mt-1 flex items-center gap-1 text-sm text-red-600">
                  <span>{errors.confirmPassword.message}</span>
                </div>
              )}
            </div>
          </div>

          <TurnstileWidget
            action="register"
            onVerify={(token) => setTurnstileToken(token)}
          />

          {/* Honeypot fields (hidden from users, visible to bots) */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
            <input
              type="text"
              name="website"
              tabIndex="-1"
              autoComplete="off"
              {...register('website')}
            />
            <input
              type="text"
              name="url"
              tabIndex="-1"
              autoComplete="off"
              {...register('url')}
            />
            <input
              type="text"
              name="phone_number"
              tabIndex="-1"
              autoComplete="off"
              {...register('phone_number')}
            />
            <input
              type="text"
              name="company"
              tabIndex="-1"
              autoComplete="off"
              {...register('company')}
            />
          </div>

          {/* Terms Acceptance */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="acceptTerms"
                type="checkbox"
                {...register('acceptTerms', {
                  required: 'You must accept the Terms of Service and Privacy Policy to create an account'
                })}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="acceptTerms" className="text-gray-700">
                I agree to the <Link to="/terms" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</Link> and <Link to="/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
              </label>
              {errors.acceptTerms && (
                <p className="mt-1 text-sm text-red-600">{errors.acceptTerms.message}</p>
              )}
            </div>
          </div>

          {/* Root Error */}
          {errors.root && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-800">{errors.root.message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading || (isTurnstileEnabled() && !turnstileToken)}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating account...</span>
                </div>
              ) : (
                'Create Account'
              )}
            </button>
          </div>

          {/* Links */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </form>

        {/* Features Preview */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-sm font-medium text-blue-800 mb-2">What you'll get:</h3>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Tax optimization and real-time calculations</li>
            <li>• Stock analysis and S&P 500 screening</li>
            <li>• Real estate portfolio management</li>
            <li>• Expense tracking with bank import</li>
            <li>• Roth IRA and retirement planning</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Register;
